import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import useDirectUpload from '../hooks/useDirectUpload';

// frontend/packages/business-console/src/pages/BuildInterviewProofInput.jsx
//
// Work item 8.9 — structured Proof step for the build interview. Captures
// verbatim testimonials, real job photos (uploaded to Cloudinary via the
// shared signed-upload path), and badges. Photos cannot ride the freeform /
// AI-extract path, and the model never invents proof (truth floor), so proof
// is a structured step. Submits a BriefProofZ payload
// { testimonials, photoAssetIds, badges }; everything is optional, so an empty
// save is equivalent to skipping.
//
// Console builds are NOT runnable in the sandbox — the deploy build is the
// gate and a founder eyeball is owed. No em-dashes in customer-visible copy.
// Upload limits, stated up front and enforced client-side (WS-F A3).
const PHOTO_MAX_MB = 5;
const PHOTO_MAX_BYTES = PHOTO_MAX_MB * 1024 * 1024;
const PHOTO_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const PHOTO_HINT = `Accepts JPG, PNG, or WebP, up to ${PHOTO_MAX_MB} MB each.`;

export default function ProofInput({ onSubmit, busy }) {
  const { upload, uploading } = useDirectUpload();
  const [testimonials, setTestimonials] = useState([{ quote: '', attribution: '' }]);
  const [photos, setPhotos] = useState([]); // [{ publicId, url }]
  const [badges, setBadges] = useState([]);
  const [badgeInput, setBadgeInput] = useState('');
  const [photoError, setPhotoError] = useState('');
  const fileRef = useRef(null);

  const setTestimonial = (i, field, val) =>
    setTestimonials((prev) => prev.map((t, j) => (j === i ? { ...t, [field]: val } : t)));
  const addTestimonial = () =>
    setTestimonials((prev) => [...prev, { quote: '', attribution: '' }]);
  const removeTestimonial = (i) =>
    setTestimonials((prev) => prev.filter((_, j) => j !== i));

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setPhotoError('');
    for (const file of files) {
      // Validate type + size up front, per file (WS-F A3).
      if (file.type && !PHOTO_ACCEPTED_TYPES.includes(file.type)) {
        setPhotoError(
          `${file.name || 'A file'} is not a supported type. Use JPG, PNG, or WebP.`
        );
        continue;
      }
      if (file.size > PHOTO_MAX_BYTES) {
        setPhotoError(
          `${file.name || 'A photo'} is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). The limit is ${PHOTO_MAX_MB} MB.`
        );
        continue;
      }
      try {
        const r = await upload(file);
        setPhotos((prev) => [...prev, { publicId: r.public_id, url: r.secure_url }]);
      } catch (err) {
        setPhotoError(
          `${file.name || 'A photo'} did not upload. Use a JPG, PNG, or WebP under ${PHOTO_MAX_MB} MB.`
        );
      }
    }
  };
  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, j) => j !== i));

  const addBadge = () => {
    const b = badgeInput.trim();
    if (!b) return;
    setBadges((prev) => (prev.length >= 20 ? prev : [...prev, b]));
    setBadgeInput('');
  };
  const removeBadge = (i) => setBadges((prev) => prev.filter((_, j) => j !== i));

  const handleSubmit = () => {
    const cleanTestimonials = testimonials
      .map((t) => ({
        quote: t.quote.trim(),
        ...(t.attribution.trim() ? { attribution: t.attribution.trim() } : {}),
      }))
      .filter((t) => t.quote);
    onSubmit({
      testimonials: cleanTestimonials,
      photoAssetIds: photos.map((p) => p.publicId),
      badges,
    });
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Customer testimonials, word for word (optional)
        </Typography>
        <Stack spacing={2}>
          {testimonials.map((t, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
              <Stack spacing={1} sx={{ flex: 1 }}>
                <TextField
                  multiline
                  minRows={2}
                  size="small"
                  label="What they said"
                  value={t.quote}
                  onChange={(e) => setTestimonial(i, 'quote', e.target.value)}
                  inputProps={{ maxLength: 1000 }}
                />
                <TextField
                  size="small"
                  label="Who said it (optional)"
                  value={t.attribution}
                  onChange={(e) => setTestimonial(i, 'attribution', e.target.value)}
                  inputProps={{ maxLength: 120 }}
                  sx={{ maxWidth: 280 }}
                />
              </Stack>
              {testimonials.length > 1 ? (
                <IconButton
                  aria-label="Remove testimonial"
                  onClick={() => removeTestimonial(i)}
                  size="small"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          ))}
        </Stack>
        <Button size="small" startIcon={<AddIcon />} onClick={addTestimonial} sx={{ mt: 1 }}>
          Add another testimonial
        </Button>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Job photos (optional)
        </Typography>
        {photos.length > 0 ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {photos.map((p, i) => (
              <Box
                key={p.publicId}
                sx={{
                  position: 'relative',
                  width: 88,
                  height: 88,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <img
                  src={p.url}
                  alt="Job"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <IconButton
                  aria-label="Remove photo"
                  size="small"
                  onClick={() => removePhoto(i)}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {PHOTO_HINT}
        </Typography>
        <Button
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <FileUploadOutlinedIcon />}
          disabled={uploading || busy}
          onClick={() => fileRef.current && fileRef.current.click()}
        >
          {uploading ? 'Uploading…' : 'Upload photos'}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          onChange={handlePhotos}
          style={{ display: 'none' }}
        />
        {photoError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {photoError}
          </Alert>
        ) : null}
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Badges or affiliations (optional)
        </Typography>
        {badges.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {badges.map((b, i) => (
              <Chip key={i} label={b} onDelete={() => removeBadge(i)} size="small" />
            ))}
          </Stack>
        ) : null}
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="e.g. BBB Accredited"
            value={badgeInput}
            onChange={(e) => setBadgeInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addBadge();
              }
            }}
            inputProps={{ maxLength: 120 }}
            sx={{ maxWidth: 280 }}
          />
          <Button onClick={addBadge} disabled={!badgeInput.trim()}>
            Add
          </Button>
        </Stack>
      </Box>

      <Box>
        <Button variant="contained" disabled={busy || uploading} onClick={handleSubmit}>
          Save proof
        </Button>
      </Box>
    </Stack>
  );
}
